import React, { useContext, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import SitewideDataContext from "../utils/sitewideContext";

/**
 * Renders a slim breadcrumb bar above main content for nav context.
 * Uses route config (title, breadcrumbParent) and optional sitewideData.breadcrumbExtra
 * for dynamic labels (e.g. site name) set by the current page.
 */
function BreadcrumbBar({ routes }) {
  const location = useLocation();
  const { sitewideData } = useContext(SitewideDataContext);

  const { segments, title } = useMemo(() => {
    const pathname = location.pathname.replace(/\/$/, "") || "/";
    const fullPath = pathname === "" ? "/" : pathname;
    const route = routes.find(
      (r) => !r.redirect && !r.hidden && r.layout + r.path === fullPath
    );
    if (!route) {
      return { segments: [{ label: "Sites", path: null }], title: "Sites" };
    }
    const currentTitle =
      sitewideData?.breadcrumbExtra?.label || route.title || route.name;
    const segments = [];
    if (route.breadcrumbParent) {
      segments.push({
        label: route.breadcrumbParent.label,
        path: route.breadcrumbParent.path,
      });
    }
    segments.push({ label: currentTitle, path: null });
    return {
      segments,
      title: [route.breadcrumbParent?.label, currentTitle].filter(Boolean).join(" · "),
    };
  }, [location.pathname, routes, sitewideData?.breadcrumbExtra?.label]);

  // Set document title for tab and accessibility
  React.useEffect(() => {
    const base = "Watch My Page";
    document.title = title ? `${title} | ${base}` : base;
  }, [title]);

  // Only show bar when there’s a hierarchy (parent + current), not a single top-level page
  if (segments.length < 2) return null;

  return (
    <nav
      className="breadcrumb-bar"
      aria-label="Breadcrumb"
    >
      <ol className="breadcrumb-bar__list">
        {segments.map((seg, i) => (
          <li key={i} className="breadcrumb-bar__item">
            {i > 0 && (
              <span className="breadcrumb-bar__sep" aria-hidden="true">
                /
              </span>
            )}
            {seg.path ? (
              <Link to={seg.path} className="breadcrumb-bar__link">
                {seg.label}
              </Link>
            ) : (
              <span className="breadcrumb-bar__current" aria-current="page">
                {seg.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default BreadcrumbBar;
